import { RenderIf } from '@/components/RenderIf'
import type { VonageRoomEvent } from '@conexasaude/vonage-express-sdk'
import {
  FunctionComponent,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  lazy,
  Suspense,
} from 'react'
import { useStyles } from './styles'
import { IS_PRODUCTION } from '@/constants'
import { ShortnedTokenData } from '../../video-call-types'
import { solveShortnedMeetToken } from '../../video-call-services'
import { MEET_SERVICES } from './types'
import { Loader, getTheme } from '@conexasaude/conexa-hero'

const VonageV2Meet = lazy(() => import('../VonageV2Meet'))
const MeetProvider = lazy(() =>
  import('@conexasaude/vonage-express-sdk').then(module => ({
    default: module.MeetProvider,
  })),
)
const Room = lazy(() =>
  import('@conexasaude/vonage-express-sdk').then(module => ({
    default: module.Room,
  })),
)

enum clinicsToVonageSdkPROD {
  MARISTA = 9429,
  ARIEL = 3291,
  CONEXA_CUIDA = 6967,
}

enum clinicsToVonageSdkHML {
  WINNERS = 8553,
  XABLAU = 8245,
  QA_DOR_ARIEL = 9669,
  DR_CONSULTA = 10343,
  ARIEL = 309,
}

const acceptedClinicsToVonageSdkHML = [
  clinicsToVonageSdkHML.WINNERS,
  clinicsToVonageSdkHML.XABLAU,
  clinicsToVonageSdkHML.QA_DOR_ARIEL,
  clinicsToVonageSdkHML.DR_CONSULTA,
  clinicsToVonageSdkHML.ARIEL,
]

const acceptedClinicsToVonageSdkPROD = [
  clinicsToVonageSdkPROD.MARISTA,
  clinicsToVonageSdkPROD.ARIEL,
  clinicsToVonageSdkPROD.CONEXA_CUIDA,
]

type MeetManagerProps = {
  meetingUrl: string
  enableTranscription: boolean
  clinicId: number
  attendingId: number
}

const MeetManager: FunctionComponent<MeetManagerProps> = ({
  enableTranscription,
  meetingUrl,
  clinicId,
}) => {
  const styles = useStyles()
  const refMeeting = useRef<HTMLIFrameElement>(null)
  const [meetSettings, setMeetSettings] = useState<ShortnedTokenData>(null)
  const [isFullScreenMeetOn, setIsFullScreenMeetOn] = useState(false)
  const { colors } = getTheme()

  const isAcceptedClinicToVonageSdk = useMemo(() => {
    if (IS_PRODUCTION) {
      return acceptedClinicsToVonageSdkPROD.includes(clinicId)
    }

    return acceptedClinicsToVonageSdkHML.includes(clinicId)
  }, [clinicId])

  const meetService = useMemo(() => {
    if (meetingUrl?.includes('express')) {
      return MEET_SERVICES.VONAGE_EXPRESS
    }

    if (meetingUrl?.includes('zoom') || meetingUrl?.includes('9999')) {
      return MEET_SERVICES.ZOOM
    }

    if (meetingUrl?.includes('v2')) {
      return MEET_SERVICES.VONAGE_V2
    }

    if (
      meetingUrl?.includes('vonage') &&
      !meetingUrl?.includes('express') &&
      !meetingUrl?.includes('v2')
    ) {
      return MEET_SERVICES.VONAGE
    }

    return MEET_SERVICES.VONAGE
  }, [meetingUrl])

  const handleSolveShortnedToken = async (meetingUrl: string) => {
    const urlObject = new URL(meetingUrl)

    const shortenedToken = urlObject.searchParams.get('shortened')

    const response = await solveShortnedMeetToken(shortenedToken)

    setMeetSettings(response)
  }

  const onReceiveMeetEvent = (event: VonageRoomEvent) => {
    if (event.type === 'changeFullscreen') {
      const fullScreenOn = event.message === 'fullscreen-on'

      setIsFullScreenMeetOn(fullScreenOn)
    }
  }

  useEffect(() => {
    if (
      [MEET_SERVICES.ZOOM, MEET_SERVICES.VONAGE_V2].includes(meetService) &&
      meetingUrl
    ) {
      handleSolveShortnedToken(meetingUrl)
    }
  }, [meetService, meetingUrl])

  useEffect(() => {
    if (meetingUrl) {
      const handleClosedApplication = () => {
        const meetFrame = refMeeting.current

        meetFrame?.contentWindow?.postMessage('CLOSED_APPLICATION', '*')
      }

      window.addEventListener('beforeunload', handleClosedApplication)

      return () => {
        window.removeEventListener('beforeunload', handleClosedApplication)
      }
    }
  }, [meetingUrl])

  return (
    <RenderIf condition={!!meetingUrl}>
      <Suspense
        fallback={
          <Loader
            size="large"
            ringColor={colors.neutral[200]}
            spinColor={colors.brand.primary[300]}
          />
        }
      >
        {meetService === MEET_SERVICES.VONAGE_EXPRESS &&
          isAcceptedClinicToVonageSdk && (
            <div
              css={[
                styles.meetContainer,
                isFullScreenMeetOn && styles.fullScreenMeet,
              ]}
            >
              <MeetProvider
                meetingUrl={meetingUrl}
                enableTranscription={enableTranscription}
                userType="PATIENT"
                env={IS_PRODUCTION ? 'PROD' : 'HML'}
                disableInlineNotifications
                listenerEvent={onReceiveMeetEvent}
              >
                <Room />
              </MeetProvider>
            </div>
          )}

        {meetService === MEET_SERVICES.VONAGE_V2 && meetSettings && (
          <div css={styles.meetContainer}>
            <VonageV2Meet
              credentialType="CREDENTIALS"
              type="PATIENT"
              env={IS_PRODUCTION ? 'PROD' : 'HML'}
              {...meetSettings}
            />
          </div>
        )}

        {[MEET_SERVICES.VONAGE_EXPRESS].includes(meetService) &&
          !isAcceptedClinicToVonageSdk && (
            <iframe
              src={meetingUrl}
              width="100%"
              height="550"
              name="meetConference"
              allow="camera; microphone; display-capture; fullscreen;"
              ref={refMeeting}
            />
          )}

        {[MEET_SERVICES.VONAGE].includes(meetService) && (
          <iframe
            src={meetingUrl}
            width="100%"
            height="550"
            name="meetConference"
            allow="camera; microphone; display-capture; fullscreen;"
            ref={refMeeting}
          />
        )}
      </Suspense>
    </RenderIf>
  )
}

export default memo(MeetManager)
